import { useState, useEffect, useCallback, useContext, ReactNode, FC, MutableRefObject, useRef } from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import { select } from 'd3-selection';

import Color from 'src/constants/color';
import { formatHumanDate } from 'src/utils/Date';
import { formatToFloat } from 'src/utils/FormatNumbers';

import ChartContext from 'src/components/LineChart/ChartContext';
import Dot from 'src/components/LineChart/Dot';
import VerticalLine from 'src/components/LineChart/VerticalLine';
import { LineChartData, LineChartPoint, XAxis } from 'src/components/LineChart/types';

import styles from 'src/components/LineChart/line-chart.less';

const TOOLTIP_GAP = 15;

interface Point extends LineChartPoint {
    x: number;
    y: number;
}

export interface TooltipData {
    points: Point[];
    id: string;
    pageX: number;
    pageY: number;
}

const hasDefinedValues = (points: Point[] | undefined) => points?.some((point) => point.value !== null);

const findNearestPoint = (x: number, pointsData: LineChartData, xAxis: XAxis): LineChartPoint => {
    let nearestPoint = pointsData[0];
    let minDistance = Math.abs(xAxis(nearestPoint.date) - x);
    pointsData.forEach((point) => {
        const distance = Math.abs(xAxis(point.date) - x);
        if (distance < minDistance) {
            minDistance = distance;
            nearestPoint = point;
        }
    });
    return nearestPoint;
};

export type TooltipPlacement = 'top' | 'right';

interface TooltipProps {
    showVerticalLine?: boolean;
    valueFormat?: (point: Point) => ReactNode;
    labelFormat?: (point: Point) => ReactNode;
    renderContent?: (data: TooltipData) => ReactNode;
    points?: Array<{
        color: Color;
    }>;
    /**
     * Позволяет использовать отдельный контейнер позиционирования тултипа, в замен контейнера из контекста
     */
    extraChartContainer?: MutableRefObject<HTMLDivElement | null>;
    /**
     * Отображает стрелку к тултипу
     */
    arrow?: boolean;
    placement?: TooltipPlacement;
    customContent?: (data: TooltipData) => JSX.Element;
}

const Tooltip: FC<TooltipProps> = ({
    showVerticalLine = false,
    valueFormat,
    labelFormat,
    renderContent,
    points,
    extraChartContainer = null,
    arrow = true,
    placement = 'top',
    customContent,
}) => {
    const { chartData, containerRef, width, height } = useContext(ChartContext);
    const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);

    const tooltipContainerRef = useRef<HTMLDivElement | null>(null);

    const verticalLineHeight = extraChartContainer?.current
        ? extraChartContainer.current.getBoundingClientRect().height
        : height;

    const isCustomTooltipContent = customContent && typeof customContent === 'function';

    const getTooltipData = useCallback(
        (x: number) => {
            const isOutOfRange = x < 0 || x > width;
            if (isOutOfRange || !containerRef.current) {
                return null;
            }

            const containerMetrics = containerRef.current.getBoundingClientRect();
            const points: Point[] = chartData.map((lineData) => {
                const point = findNearestPoint(x, lineData.data, lineData.xAxis);
                const additionalData = point.additional ? { additional: point.additional } : {};
                return {
                    date: point.date,
                    rawDate: point.rawDate,
                    value: point.value,
                    x: lineData.xAxis(point.date),
                    y: lineData.yAxis(point.value),
                    ...additionalData,
                };
            });
            const isFake = points.some((p) => p.additional?.isFake);

            /* select the highest point to place the tip above it */
            const maxY = Math.min(...points.map((item) => item.y));

            return {
                points,
                id: points.map((point) => point.rawDate).join('_'),
                pageX: window.scrollX + containerMetrics.left + points[0].x,
                pageY: window.scrollY + containerMetrics.top + maxY - TOOLTIP_GAP,
                isFake,
            };
        },
        [chartData, containerRef, width]
    );

    useEffect(() => {
        if (tooltipData && tooltipContainerRef.current) {
            const tooltipContainerWidth = tooltipContainerRef.current.getBoundingClientRect().width;
            // при правом положении, тултип не должен выходить за границы контейнера
            const pageX =
                tooltipData?.pageX + tooltipContainerWidth > width && placement === 'right'
                    ? tooltipData?.pageX - tooltipContainerWidth
                    : tooltipData?.pageX;

            select(tooltipContainerRef.current).style('top', `${tooltipData?.pageY}px`).style('left', `${pageX}px`);
        }
    }, [placement, tooltipData, width]);

    useEffect(() => {
        const container = extraChartContainer?.current ?? containerRef.current;

        const mouseMoveHandler = (event: MouseEvent) => {
            const newTooltipData = getTooltipData(event.offsetX);
            if (
                tooltipData?.id !== newTooltipData?.id &&
                hasDefinedValues(newTooltipData?.points) &&
                !newTooltipData?.isFake
            ) {
                setTooltipData(newTooltipData);
            }
        };
        const mouseLeaveHandler = (event: MouseEvent) => {
            const relatedElement = event.relatedTarget as HTMLElement;
            if (relatedElement && !relatedElement.closest(`.${styles.lineChartTip}`)) {
                setTooltipData(null);
            }
        };

        if (container) {
            container.addEventListener('mousemove', mouseMoveHandler);
            container.addEventListener('mouseleave', mouseLeaveHandler);
        }

        return () => {
            if (container) {
                container.removeEventListener('mousemove', mouseMoveHandler);
                container.removeEventListener('mouseleave', mouseLeaveHandler);
            }
        };
    }, [containerRef, width, getTooltipData, tooltipData, extraChartContainer]);

    const mouseLeaveHandler = () => {
        setTooltipData(null);
    };

    if (!tooltipData) {
        return null;
    }

    const tooltipContent = isCustomTooltipContent ? (
        customContent(tooltipData)
    ) : (
        <>
            <div className={styles.lineChartTipDate}>
                {labelFormat ? labelFormat(tooltipData.points[0]) : formatHumanDate(tooltipData.points[0].date)}
            </div>
            {renderContent
                ? renderContent(tooltipData)
                : tooltipData.points.map((point, index) => (
                      <div className={styles.lineChartTipValue} key={index}>
                          {valueFormat ? valueFormat(point) : formatToFloat(point.value)}
                      </div>
                  ))}
        </>
    );

    return (
        <>
            {showVerticalLine && <VerticalLine height={verticalLineHeight} x={tooltipData?.points[0].x} />}
            {tooltipData?.points.map((point, index) => (
                <Dot key={index} x={point.x} y={point.y} color={points ? points[index].color : undefined} />
            ))}
            {ReactDOM.createPortal(
                <div
                    className={classnames(styles.lineChartTip, {
                        [styles.lineChartTipArrow]: arrow,
                        [styles.lineChartTipPlacementTop]: placement === 'top',
                        [styles.lineChartTipPlacementRight]: placement === 'right',
                        [styles.lineChartTipDefaultContent]: !isCustomTooltipContent,
                    })}
                    onMouseLeave={mouseLeaveHandler}
                    ref={tooltipContainerRef}
                >
                    {tooltipContent}
                </div>,
                document.body
            )}
        </>
    );
};

export default Tooltip;
